import { queryOptions } from "@tanstack/react-query";
import {
  isStartFailed,
  orderStatusRefetchInterval,
} from "@frontend-common/start-guide/start-guide-logic";
import { api, type OrderStatus } from "@/lib/server";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";

export { isStartFailed };

export type PaymentCompleteTarget =
  | "/wash-start-guide"
  | "/invoice-question-screen";

/**
 * Where a finished payment goes: the wash start guide for a washer when the
 * flag is on and the purchase has an order, today's invoice question otherwise
 * (dryers, older cash-agents, an order status that can't be read).
 */
export function resolvePaymentCompleteTarget({
  washStartGuide,
  status,
}: {
  washStartGuide: boolean;
  status: OrderStatus | null;
}): PaymentCompleteTarget {
  return washStartGuide && status?.machine.type === "mosogep"
    ? "/wash-start-guide"
    : "/invoice-question-screen";
}

const reportedFailures = new Set<string>();

function reportFailureOnce(orderId: string, status: OrderStatus) {
  if (!isStartFailed(status) || reportedFailures.has(orderId)) {
    return;
  }
  reportedFailures.add(orderId);
  void recordTrackedEvent({
    eventType: "wash_start_failed",
    machineId: status.machine.id,
    payload: {
      orderId,
      orderStatus: status.status,
      spinningState: status.spinningState,
      sessionId: status.sessionId,
    },
  });
}

/** Polls every 2 s until the drum spins or the start window runs out. */
export const orderStatusQuery = (orderId: string) =>
  queryOptions({
    queryKey: ["order-status", orderId],
    queryFn: async () => {
      const status = await api.orderStatus(orderId);
      reportFailureOnce(orderId, status);
      return status;
    },
    refetchInterval: (query) => orderStatusRefetchInterval(query.state.data),
    // a failed poll keeps the last answer; the next one tries again
    retry: false,
  });
